import { useFeatureDetectionContext } from "@/components/common/feature-detection-context";
import {
  PanoPoint,
  PreviewFeatureRenderer,
} from "@/components/r3f/renderers/preview-feature-renderer";
import { PointCloudObject } from "@/object-cache";
import { ToolName } from "@/store/ui/ui-slice";
import {
  computePanoFeatures,
  computePointCloudFeatures,
} from "@/utils/feature-detection";
import {
  getPickedNormal,
  getPickedPoint,
} from "@faro-lotv/app-component-toolbox";
import {
  InfiniteLine,
  isDepthImageIntersection,
  TiledPano,
} from "@faro-lotv/lotv";
import { ThreeEvent, useThree } from "@react-three/fiber";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  type JSX,
} from "react";
import { Vector3 } from "three";

export type PointerMovePreviewHandlerRef = {
  /** Forward model hovered event to the active tool */
  onModelHovered(ev: ThreeEvent<PointerEvent>): void;
};

type PointerMovePreviewHandlerProps = {
  // Point cloud object to use compute the plane orientation if normal is not provided by raycaster
  pointcloud?: PointCloudObject;
};

/**
 * @returns A preview handler that shows the model geometric feature hovered under the pointer.
 */
export const PointerMovePreviewHandler = forwardRef<
  PointerMovePreviewHandlerRef,
  PointerMovePreviewHandlerProps
>(function PointerMovePreviewTool({ pointcloud }, ref): JSX.Element | null {
  const [currentPoint, setCurrentPoint] = useState<Vector3>();

  const { edgeCornerDetection, setFeatures } = useFeatureDetectionContext();
  const [normal, setNormal] = useState<Vector3>();
  const [edge, setEdge] = useState<InfiniteLine>();
  const [corner, setCorner] = useState<Vector3>();

  const { camera } = useThree();

  useImperativeHandle(ref, () => ({
    onModelHovered(ev: ThreeEvent<PointerEvent>) {
      if (!(ev.object instanceof TiledPano)) ev.stopPropagation();
      const position = getPickedPoint(ev);
      const normal = getPickedNormal(ev);
      const panoPoint: PanoPoint | undefined =
        ev.object instanceof TiledPano && isDepthImageIntersection(ev)
          ? {
              pano: ev.object,
              depthImagePoint: ev.depthImagePoint,
            }
          : undefined;
      setCurrentPoint(position);

      async function updatePreviews(): Promise<void> {
        let edge = undefined;
        let corner = undefined;

        // use the provided normal if available
        let planeNormal = normal;

        if (panoPoint) {
          if (
            isPanoPositionGoodForFeatureDetection(
              position,
              planeNormal,
              camera.position,
            )
          ) {
            const result = await computePanoFeatures(
              panoPoint.pano,
              panoPoint.depthImagePoint,
              PANO_SELECTION_OFFSET,
              SELECTION_RADIUS,
            );
            planeNormal = result?.normal;
            edge = result?.edge;
            corner = result?.corner;
          }
        } else if (pointcloud) {
          const result = await computePointCloudFeatures(
            pointcloud,
            position,
            SELECTION_RADIUS,
          );
          planeNormal = result?.normal;

          edge = result?.edge;
          corner = result?.corner;
        }
        setFeatures(planeNormal, edge, corner);
        setNormal(planeNormal);
        setEdge(edge);
        setCorner(corner);
      }
      updatePreviews().catch(console.error);
    },
  }));

  if (!currentPoint || !normal) {
    return null;
  }

  return (
    <PreviewFeatureRenderer
      position={currentPoint}
      normal={normal}
      edge={edgeCornerDetection ? edge : undefined}
      corner={edgeCornerDetection ? corner : undefined}
    />
  );
});

// Extract local geometry features by checking neighbourhood points within 0.175 meters radius
export const SELECTION_RADIUS = 0.175;

// Collect neighbour point in Pano depth image in a square around the center point within
// 40 pixels offset in both directions
const PANO_SELECTION_OFFSET = 40;

/**
 * A hook that sets the pointer move preview visibility based on the active tool
 *
 * @param activeTool The active tool name
 */
export function usePointerMovePreview(activeTool: ToolName | null): void {
  const { setNormalDetection } = useFeatureDetectionContext();
  // Enable the pointer move preview tool when the active tool is measurement or analysis
  useEffect(
    () =>
      setNormalDetection(
        activeTool === ToolName.measurement || activeTool === ToolName.analysis,
      ),
    [activeTool, setNormalDetection],
  );
}

// Constant for the angle threshold 20 degrees
const angleThreshold = 0.349066;
const angleThresholdCosSquared =
  Math.cos(angleThreshold) * Math.cos(angleThreshold);
// temporary vector to avoid repleating allocations
const tmpLineOfSight = new Vector3();

// Check if the pano position is good for feature detection.
// The pano depth becomes less accurate when the pixel is further away from the camera.
// When local normal is aligned with the line of sight, the depth is more reliable.
// Based on experiments with custom data, following rules are used:
//  - pano points with 8 meters are generally ok
//  - pano points 20 meters away from camera are not reliable
//  - for points between 8-20 meters, points with local normal aligned to line of
//    sight (angle < 20 degree) are ok
function isPanoPositionGoodForFeatureDetection(
  panoPosition: Vector3,
  panoNormal: Vector3 | undefined,
  cameraPosition: Vector3,
): boolean {
  const minDistanceSquared = 64.0;
  const maxDistanceSquared = 400.0;

  const lineOfSight = tmpLineOfSight.subVectors(panoPosition, cameraPosition);
  const distanceSquared = lineOfSight.lengthSq();
  if (distanceSquared < minDistanceSquared) {
    // Close enough, always ok
    return true;
  }
  if (distanceSquared > maxDistanceSquared) {
    // Too far, not reliable
    return false;
  }
  if (!panoNormal) {
    // No normal provided, assume not reliable
    return false;
  }
  // Normal is provided, check the angle with the line of sight
  // should be less than 20 degrees
  const dot = panoNormal.dot(lineOfSight);
  const dotSq = dot * dot;
  const lengthSqProduct = panoNormal.lengthSq() * lineOfSight.lengthSq();
  return dotSq > angleThresholdCosSquared * lengthSqProduct;
}
